/**
 * Chakra UI Theme Extension
 * Integrates design tokens into Chakra's theme system
 *
 * Aesthetic: "Command Center" - Professional, technical, polished
 */

import { extendTheme, type ThemeConfig } from '@chakra-ui/react'
import { colors, typography, spacing, radii, shadows, transitions } from './tokens'

const config: ThemeConfig = {
  initialColorMode: 'system',
  useSystemColorMode: false,
}

export const theme = extendTheme({
  config,
  colors: {
    brand: colors.brand,
    severity: colors.severity,
    accent: colors.accent,
  },
  fonts: typography.fonts,
  fontSizes: typography.fontSizes,
  fontWeights: typography.fontWeights,
  lineHeights: typography.lineHeights,
  space: spacing,
  radii,
  shadows,
  transition: {
    duration: transitions.duration,
    easing: transitions.easing,
  },
  styles: {
    global: (props: { colorMode: string }) => ({
      body: {
        bg: props.colorMode === 'dark' ? colors.ui.background.dark : colors.ui.background.light,
        color: props.colorMode === 'dark' ? colors.ui.text.primary.dark : colors.ui.text.primary.light,
        fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"', // IBM Plex alternates
      },
      // Smooth scrolling
      html: {
        scrollBehavior: 'smooth',
      },
      // Better focus rings
      '*:focus-visible': {
        outline: 'none',
        boxShadow: props.colorMode === 'dark'
          ? `0 0 0 2px ${colors.ui.background.dark}, 0 0 0 4px ${colors.accent.dark}`
          : `0 0 0 2px ${colors.ui.background.light}, 0 0 0 4px ${colors.accent.light}`,
      },
      // Selection color
      '::selection': {
        bg: props.colorMode === 'dark' ? 'rgba(34, 211, 238, 0.3)' : 'rgba(14, 165, 233, 0.2)',
      },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: typography.fontWeights.medium,
        borderRadius: radii.md,
        transition: `all ${transitions.duration.normal} cubic-bezier(0.4, 0, 0.2, 1)`,
        _hover: {
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0)',
        },
      },
      variants: {
        solid: (props: { colorMode: string }) => ({
          boxShadow: props.colorMode === 'dark' ? shadows.dark.sm : shadows.sm,
        }),
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: radii.base,
        fontWeight: typography.fontWeights.semibold,
        textTransform: 'uppercase',
        fontFamily: typography.fonts.mono,
        fontSize: '0.7rem',
        letterSpacing: '0.025em',
      },
    },
    Card: {
      baseStyle: (props: { colorMode: string }) => ({
        container: {
          bg: props.colorMode === 'dark' ? colors.ui.card.dark : colors.ui.card.light,
          borderRadius: radii.lg,
          boxShadow: props.colorMode === 'dark' ? shadows.dark.card : shadows.base,
          borderWidth: '1px',
          borderColor: props.colorMode === 'dark' ? colors.ui.borderSubtle.dark : colors.ui.borderSubtle.light,
          transition: `all ${transitions.duration.normal} ease`,
        },
      }),
    },
    Input: {
      baseStyle: {
        field: {
          fontFamily: typography.fonts.body,
          transition: `all ${transitions.duration.fast} ease`,
        },
      },
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            color: props.colorMode === 'dark' ? colors.ui.text.primary.dark : colors.ui.text.primary.light,
            bg: props.colorMode === 'dark' ? colors.ui.card.dark : colors.ui.card.light,
            borderColor: props.colorMode === 'dark' ? colors.ui.border.dark : colors.ui.border.light,
            _placeholder: {
              color: props.colorMode === 'dark' ? colors.ui.text.muted.dark : colors.ui.text.muted.light,
            },
            _hover: {
              borderColor: props.colorMode === 'dark' ? colors.accent.dark : colors.accent.light,
            },
            _focus: {
              borderColor: props.colorMode === 'dark' ? colors.accent.dark : colors.accent.light,
              boxShadow: props.colorMode === 'dark'
                ? `0 0 0 1px ${colors.accent.dark}`
                : `0 0 0 1px ${colors.accent.light}`,
            },
          },
        }),
      },
    },
    Table: {
      variants: {
        simple: (props: { colorMode: string }) => ({
          th: {
            fontFamily: typography.fonts.body,
            fontWeight: typography.fontWeights.semibold,
            textTransform: 'none',
            letterSpacing: '0',
            fontSize: typography.fontSizes.xs,
            color: props.colorMode === 'dark' ? colors.ui.text.secondary.dark : colors.ui.text.secondary.light,
            borderColor: props.colorMode === 'dark' ? colors.ui.border.dark : colors.ui.border.light,
          },
          td: {
            fontSize: typography.fontSizes.sm,
            borderColor: props.colorMode === 'dark' ? colors.ui.borderSubtle.dark : colors.ui.borderSubtle.light,
          },
          tr: {
            transition: `background ${transitions.duration.fast} ease`,
            _hover: {
              bg: props.colorMode === 'dark' ? colors.ui.cardHover.dark : colors.ui.cardHover.light,
            },
          },
        }),
      },
    },
    Drawer: {
      baseStyle: (props: { colorMode: string }) => ({
        dialog: {
          bg: props.colorMode === 'dark' ? colors.ui.card.dark : colors.ui.card.light,
        },
        header: {
          fontFamily: typography.fonts.heading,
          fontWeight: typography.fontWeights.semibold,
        },
      }),
    },
    Modal: {
      baseStyle: (props: { colorMode: string }) => ({
        dialog: {
          bg: props.colorMode === 'dark' ? colors.ui.card.dark : colors.ui.card.light,
          borderRadius: radii.lg,
        },
        header: {
          fontFamily: typography.fonts.heading,
          fontWeight: typography.fontWeights.semibold,
        },
      }),
    },
    Heading: {
      baseStyle: {
        fontFamily: typography.fonts.heading,
        fontWeight: typography.fontWeights.semibold,
      },
    },
    Text: {
      baseStyle: {
        fontFamily: typography.fonts.body,
      },
    },
    Tooltip: {
      baseStyle: (props: { colorMode: string }) => ({
        bg: props.colorMode === 'dark' ? colors.ui.card.dark : 'gray.800',
        color: props.colorMode === 'dark' ? colors.ui.text.primary.dark : 'white',
        fontSize: typography.fontSizes.xs,
        fontWeight: typography.fontWeights.medium,
        borderRadius: radii.md,
        px: 3,
        py: 2,
        boxShadow: shadows.lg,
      }),
    },
  },
})

export * from './tokens'
export type { ThemeConfig }
